import { z } from 'zod';

export const dataModeSchema = z.enum(['LIVE_LOCAL', 'SYNTHETIC_DEMO']);
export const provenanceSchema = z.enum([
  'AUTHORIZED_SEED',
  'SEEDED',
  'LIVE_LOCAL',
  'SYNTHETIC_DEMO',
]);
export const personNatureSchema = z.enum(['HUMAN', 'PERMANENT_AI', 'AIZUBI']);
export const personStatusSchema = z.enum([
  'OFFLINE',
  'AVAILABLE',
  'WORKING',
  'WAITING_HUMAN',
  'WAITING_TOOL',
  'QA_REVIEW',
  'COOLDOWN',
  'BLOCKED',
  'SUSPENDED',
  'OFFBOARDED',
]);
export const jobStatusSchema = z.enum([
  'DRAFT',
  'READY',
  'QUEUED',
  'ASSIGNED',
  'RUNNING',
  'WAITING_HUMAN',
  'WAITING_HUMAN_EXPIRED',
  'WAITING_TOOL',
  'WAITING_DEPENDENCY',
  'QA_REVIEW',
  'APPROVED',
  'CHANGES_REQUESTED',
  'COMPLETED',
  'BLOCKED',
  'FAILED',
  'RETRY_QUEUED',
  'CANCELLED',
  'SUSPENDED',
]);
export const severitySchema = z.enum(['INFO', 'ATTENTION', 'BLOCKER', 'INCIDENT']);
export const riskSchema = z.enum(['R0', 'R1', 'R2', 'R3', 'R4']);
export const dataClassSchema = z.enum(['PUBLIC', 'INTERNAL', 'SYNTHETIC', 'CUSTOMER', 'SECRET']);
export const accessLevelSchema = z.enum(['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6']);
export const providerStatusSchema = z.enum([
  'UNKNOWN',
  'AVAILABLE',
  'LOW',
  'EXHAUSTED',
  'COOLDOWN',
  'AUTH_REQUIRED',
  'POLICY_BLOCKED',
  'DISABLED',
]);
export const grantStatusSchema = z.enum([
  'REQUESTED',
  'APPROVED',
  'ACTIVE',
  'ACTIVE_BOUNDED',
  'EXPIRING',
  'EXPIRED',
  'DENIED',
  'REVOKED',
  'SUSPENDED',
  'PREVIEW_REVOKED',
]);
export const gateStatusSchema = z.enum([
  'PROPOSED',
  'PENDING',
  'APPROVED_ONCE',
  'APPROVED_BOUNDED',
  'REJECTED',
  'RETURNED',
  'EXPIRED',
  'REVOKED',
  'CONSUMED',
]);
export const runnerNodeStateSchema = z.enum([
  'UNREGISTERED',
  'ENROLLING',
  'ONLINE',
  'DRAINING',
  'OFFLINE',
  'DEGRADED',
  'QUARANTINED',
  'REVOKED',
]);
export const leaseStateSchema = z.enum([
  'PENDING',
  'ACTIVE',
  'RELEASED',
  'REVOKED',
  'TIMED_OUT',
  'NODE_LOST',
]);
export const incidentStatusSchema = z.enum(['OPEN', 'CONTAINED', 'RESOLVED']);
export const pauseScopeSchema = z.enum(['PERSON', 'DEPARTMENT', 'COMPANY']);
export const taskDomainSchema = z.enum([
  'SOFTWARE_ENGINEERING',
  'RESEARCH',
  'DOCUMENTATION',
  'OPERATIONS',
  'POLICY_REVIEW',
]);
export const runnerAdapterKeySchema = z.enum([
  'FAKE_JSONL',
  'OPENAI_CODEX',
  'ANTHROPIC_CLAUDE_CODE',
  'ZAI_OPENCODE',
]);
export const providerPolicyReviewStatusSchema = z.enum([
  'SAFE_TO_SMOKE_TEST',
  'BLOCKED_BY_POLICY',
  'BLOCKED_BY_TECHNICAL',
]);
export const capacitySourceSchema = z.enum([
  'CLI',
  'OFFICIAL_DASHBOARD',
  'PROVIDER_RESPONSE',
  'OPERATOR',
  'SYNTHETIC_HARNESS',
  'NO_MEASUREMENT',
]);
export const capacityRemainingUnitSchema = z.enum(['MESSAGES', 'CREDITS', 'PERCENT', 'UNKNOWN']);
export const capacityConfidenceSchema = z.enum([
  'UNKNOWN',
  'OPERATOR_CONFIRMED',
  'ESTIMATED',
  'EXACT',
]);
export const runnerEnrollmentStatusSchema = z.enum([
  'NOT_ENROLLED',
  'CHALLENGE_ISSUED',
  'ENROLLED',
  'REVOKED',
]);
export const runnerAllowedToolSchema = z.enum(['read', 'write']);

export const personSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  nature: personNatureSchema,
  role: z.string().min(1),
  managerId: z.string().nullable(),
  departmentId: z.string().min(1),
  status: personStatusSchema,
  statusLabel: z.string().min(1),
  competency: z.string().min(1),
  levelCeiling: z.enum(['L1', 'L2', 'L3', 'L4']),
  accessLevels: z.array(accessLevelSchema),
  currentJobId: z.string().nullable(),
  provenance: provenanceSchema,
});

export const departmentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  shortName: z.string().min(1),
  leadId: z.string().min(1),
  objective: z.string().min(1),
  health: z.enum(['HEALTHY', 'ATTENTION', 'BLOCKED']),
  queueCount: z.number().int().nonnegative(),
  oldestItemHours: z.number().nonnegative(),
  nextDeadline: z.string().min(1),
  memberIds: z.array(z.string()),
  tools: z.array(z.string()),
  throughput: z.array(z.number().nonnegative()),
  provenance: provenanceSchema.default('SEEDED'),
});

export const jobSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  objective: z.string().min(1).optional(),
  family: z.string().min(1),
  departmentId: z.string().min(1),
  ownerId: z.string().min(1),
  status: jobStatusSchema,
  priority: z.enum(['P0', 'P1', 'P2', 'P3']),
  risk: riskSchema,
  dataClass: dataClassSchema,
  deadline: z.string().min(1),
  budgetCents: z.number().int().nonnegative().default(0),
  blockedReason: z.string().nullable(),
  nextOwnerId: z.string().nullable(),
  qaReviewerId: z.string().nullable(),
  acceptanceEvidence: z.array(z.string()).default([]),
  queuedAt: z.string().datetime().nullable().default(null),
  completedAt: z.string().datetime().nullable().default(null),
  retryCount: z.number().int().nonnegative().default(0),
  retryLimit: z.number().int().nonnegative().default(0),
  humanMinutes: z.number().int().nonnegative().default(0),
  provenance: provenanceSchema,
});

export const grantSchema = z.object({
  id: z.string().min(1),
  personId: z.string().min(1),
  resourceId: z.string().min(1).default('UNRESOLVED'),
  resource: z.string().min(1),
  permission: z.string().min(1),
  purpose: z.string().min(1).default('Authorized seeded responsibility'),
  accessLevel: accessLevelSchema,
  dataClass: dataClassSchema.default('INTERNAL'),
  status: grantStatusSchema,
  expiry: z.string().min(1),
  jobId: z.string().nullable().default(null),
  requestedBy: z.string().min(1).default('HUM-001'),
  approverId: z.string().nullable().default(null),
  effectiveAt: z.string().datetime().nullable().default(null),
  reviewAt: z.string().datetime().nullable().default(null),
  revocationTrigger: z.string().min(1).default('scope change or review expiry'),
  evidenceRef: z.string().min(1).default('seed/grant'),
  provenance: provenanceSchema,
});

export const runnerSessionSchema = z.object({
  id: z.string().min(1),
  personId: z.string().min(1),
  runnerNodeId: z.string().min(1),
  providerPoolId: z.string().min(1),
  jobId: z.string().min(1),
  status: z.enum([
    'STARTING',
    'ACTIVE',
    'COMPLETED',
    'FAILED',
    'QUARANTINED',
    'SIMULATED_ACTIVE',
    'SIMULATED_REVOKED',
  ]),
  tool: z.string().min(1),
  provenance: provenanceSchema,
});

export const providerPoolSchema = z.object({
  id: z.string().min(1),
  provider: z.string().min(1),
  plan: z.string().min(1),
  tool: z.string().min(1),
  status: providerStatusSchema,
  confidence: capacityConfidenceSchema,
  remainingLabel: z.string().nullable(),
  resetLabel: z.string().nullable(),
  policy: z.string().min(1),
  runnerLabel: z.string().min(1),
  adapterKey: runnerAdapterKeySchema.default('FAKE_JSONL'),
  enabled: z.boolean().default(false),
  policyReviewStatus: providerPolicyReviewStatusSchema.default('BLOCKED_BY_TECHNICAL'),
  policyReviewedAt: z.string().datetime().nullable().default(null),
  taskDomains: z.array(taskDomainSchema).default([]),
  allowedDataClasses: z.array(dataClassSchema).default(['SYNTHETIC']),
  capacitySource: capacitySourceSchema.default('NO_MEASUREMENT'),
  remainingValue: z.number().nonnegative().nullable().default(null),
  remainingUnit: capacityRemainingUnitSchema.default('UNKNOWN'),
  resetsAt: z.string().datetime().nullable().default(null),
  weeklyResetsAt: z.string().datetime().nullable().default(null),
  noSpend: z.literal(true).default(true),
  provenance: provenanceSchema,
});

export const approvalGateSchema = z.object({
  id: z.string().min(1),
  jobId: z.string().nullable().default(null),
  type: z.enum([
    'HG-IDENTITY',
    'HG-ACCOUNT',
    'HG-EXTERNAL-SEND',
    'HG-SPEND',
    'HG-CONTRACT',
    'HG-CUSTOMER-DATA',
    'HG-PRODUCTION',
    'HG-EXCEPTION',
  ]),
  title: z.string().min(1),
  action: z.string().min(1),
  requesterId: z.string().min(1),
  approverId: z.string().nullable(),
  affectedPersonId: z.string().min(1),
  consequence: z.string().min(1),
  evidence: z.array(z.string()).min(1),
  payloadPreview: z.string().min(1),
  payloadHash: z.string().min(1).default('UNBOUND'),
  approvedPayloadHash: z.string().nullable().default(null),
  risk: riskSchema,
  dataClass: dataClassSchema,
  budgetCents: z.number().int().nonnegative().default(0),
  scope: z.record(z.string(), z.unknown()).default({}),
  expiresLabel: z.string().min(1),
  expiresAt: z.string().datetime().nullable().default(null),
  consumedAt: z.string().datetime().nullable().default(null),
  status: gateStatusSchema,
  provenance: provenanceSchema,
});

export const resourceTypeSchema = z.enum([
  'DOMAIN',
  'SUBDOMAIN',
  'VPS',
  'REPOSITORY',
  'MAILBOX',
  'PLATFORM_ACCOUNT',
  'SUBSCRIPTION',
  'WORKSTATION',
  'RUNNER_NODE',
  'TOOL',
  'TEMPLATE',
  'LICENSE',
  'DATASET',
  'PROJECT_RECORD',
]);

export const resourceAssetSchema = z.object({
  id: z.string().min(1),
  type: resourceTypeSchema,
  label: z.string().min(1),
  classification: dataClassSchema,
  ownership: z.enum(['OWNER_CONFIRMED', 'OWNER_CLEARED', 'UNKNOWN']),
  usage: z.enum(['ACTIVE_EXISTING', 'POTENTIAL', 'INTERNAL_ONLY', 'UNKNOWN']),
  evidenceRef: z.string().min(1),
  notes: z.string().min(1),
  secretReferenceState: z.enum(['NONE', 'CONFIGURED']),
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  provenance: provenanceSchema,
});

export const accessPolicySchema = z.object({
  level: accessLevelSchema,
  meaning: z.string().min(1),
  boundary: z.string().min(1),
  humanRequired: z.boolean(),
  policyVersion: z.number().int().positive(),
});

export const emergencyDrillSchema = z.object({
  id: z.string().min(1),
  status: z.enum(['NOT_RUN', 'RUNNING', 'PASSED', 'FAILED']),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  correlationId: z.string().nullable(),
  affectedJobIds: z.array(z.string()),
  auditEventIds: z.array(z.string()),
  evidencePath: z.string().nullable(),
  synthetic: z.literal(true),
});

export const domainEventSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  occurredAt: z.string().datetime(),
  actorId: z.string().min(1),
  actorSessionId: z.string().nullable().default(null),
  subjectType: z.string().min(1).default('UNKNOWN'),
  subjectId: z.string().min(1),
  correlationId: z.string().min(1),
  causationId: z.string().nullable().default(null),
  risk: riskSchema.default('R0'),
  dataClass: dataClassSchema.default('INTERNAL'),
  payload: z.record(z.string(), z.unknown()).default({}),
  policyVersion: z.number().int().positive().nullable().default(null),
  decisionReasons: z.array(z.string()).default([]),
  severity: severitySchema,
  message: z.string().min(1),
  provenance: provenanceSchema,
  schemaVersion: z.literal(1),
});

export const notificationSchema = z.object({
  id: z.string().min(1),
  severity: severitySchema,
  title: z.string().min(1),
  detail: z.string().min(1),
  nextAction: z.string().min(1),
  sourceId: z.string().min(1),
  recipientId: z.string().min(1).default('HUM-001'),
  acknowledgedAt: z.string().datetime().nullable().default(null),
  provenance: provenanceSchema.default('SEEDED'),
});

export const queueProjectionSchema = z.object({
  id: z.string().min(1),
  departmentId: z.string().min(1),
  orderingPolicy: z.literal('priority_deadline_age'),
  paused: z.boolean(),
  itemIds: z.array(z.string()),
  oldestItemHours: z.number().nonnegative().nullable(),
  provenance: provenanceSchema,
});

export const jobDependencySchema = z.object({
  id: z.string().min(1),
  predecessorJobId: z.string().min(1),
  successorJobId: z.string().min(1),
  condition: z.string().min(1),
  status: z.enum(['PENDING', 'SATISFIED', 'BLOCKED', 'CANCELLED']),
  blockedReason: z.string().nullable(),
  provenance: provenanceSchema,
});

export const assignmentProjectionSchema = z.object({
  id: z.string().min(1),
  personId: z.string().min(1),
  jobId: z.string().min(1),
  scope: z.string().min(1),
  status: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED', 'COMPLETED']),
  effectiveAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  qaReviewerId: z.string().nullable(),
  accessInherited: z.literal(false),
  provenance: provenanceSchema,
});

export const leaseProjectionSchema = z.object({
  id: z.string().min(1),
  grantId: z.string().min(1),
  jobId: z.string().min(1),
  runnerNodeId: z.string().min(1),
  state: leaseStateSchema,
  startedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime(),
  provenance: provenanceSchema,
});

export const runnerNodeSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  state: runnerNodeStateSchema,
  trustClass: z.string().min(1),
  capabilities: z.array(z.string()),
  lastHeartbeatAt: z.string().datetime().nullable(),
  heartbeatFresh: z.boolean(),
  currentJobId: z.string().nullable(),
  protocolVersion: z.literal(1).default(1),
  enrollmentStatus: runnerEnrollmentStatusSchema.default('NOT_ENROLLED'),
  allowedRoots: z.array(z.string().min(1)).default([]),
  keyFingerprint: z.string().nullable().default(null),
  adapterKeys: z.array(runnerAdapterKeySchema).default([]),
  controlReason: z.string().nullable().default(null),
  provenance: provenanceSchema,
});

export const runnerJobEnvelopePayloadSchema = z.object({
  protocolVersion: z.literal(1),
  envelopeId: z.string().min(1),
  jobId: z.string().min(1),
  leaseId: z.string().min(1),
  runnerNodeId: z.string().min(1),
  providerPoolId: z.string().min(1),
  adapterKey: runnerAdapterKeySchema,
  taskDomain: taskDomainSchema,
  dataClass: dataClassSchema,
  workspaceRoot: z.string().min(1),
  objective: z.string().min(1),
  acceptanceCriteria: z.array(z.string().min(1)).min(1),
  allowedTools: z.array(runnerAllowedToolSchema).min(1),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  nonce: z.string().min(16),
  spendAllowed: z.literal(false),
  thirdPartyServiceMode: z.literal(false),
  syntheticDataOnly: z.boolean(),
  outputSchema: z.record(z.string(), z.unknown()).nullable().default(null),
});

export const signedRunnerJobEnvelopeSchema = z.object({
  payload: runnerJobEnvelopePayloadSchema,
  signature: z.string().min(32),
  keyId: z.string().min(1),
  algorithm: z.literal('Ed25519'),
});

export const runnerHeartbeatPayloadSchema = z.object({
  protocolVersion: z.literal(1),
  runnerNodeId: z.string().min(1),
  observedAt: z.string().datetime(),
  nonce: z.string().min(16),
  state: z.enum(['ONLINE', 'DRAINING', 'DEGRADED']),
  capabilities: z.array(z.string().min(1)),
  adapterKeys: z.array(runnerAdapterKeySchema),
  allowedRoots: z.array(z.string().min(1)),
});

export const signedRunnerHeartbeatSchema = z.object({
  payload: runnerHeartbeatPayloadSchema,
  signature: z.string().min(32),
  algorithm: z.literal('Ed25519'),
});

export const runnerControlPayloadSchema = z.object({
  protocolVersion: z.literal(1),
  instructionId: z.string().min(1),
  runnerNodeId: z.string().min(1),
  jobId: z.string().min(1),
  grantId: z.string().min(1),
  action: z.enum(['CANCEL_OR_DRAIN', 'DRAIN', 'QUARANTINE']),
  reason: z.string().min(1),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  nonce: z.string().min(16),
});

export const signedRunnerControlEnvelopeSchema = z.object({
  payload: runnerControlPayloadSchema,
  signature: z.string().min(32),
  keyId: z.string().min(1),
  algorithm: z.literal('Ed25519'),
});

export const changedFileManifestEntrySchema = z.object({
  path: z.string().min(1),
  operation: z.enum(['CREATED', 'MODIFIED', 'DELETED']),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/u)
    .nullable(),
  bytes: z.number().int().nonnegative(),
});

export const runnerLifecycleEventSchema = z.object({
  protocolVersion: z.literal(1),
  event: z.enum(['STARTED', 'PROGRESS', 'CAPACITY', 'OUTPUT', 'COMPLETED', 'FAILED']),
  occurredAt: z.string().datetime(),
  message: z.string().min(1),
  data: z.record(z.string(), z.unknown()).default({}),
});

export const runnerExecutionResultSchema = z.object({
  protocolVersion: z.literal(1),
  envelopeId: z.string().min(1),
  jobId: z.string().min(1),
  sessionStatus: z.enum(['COMPLETED', 'FAILED', 'CANCELLED', 'QUARANTINED', 'WAITING_TOOL']),
  exitCode: z.number().int().nullable(),
  stdout: z.array(runnerLifecycleEventSchema),
  stderr: z.array(z.string()),
  changedFiles: z.array(changedFileManifestEntrySchema),
  quarantineReason: z.string().nullable(),
  capacityStatus: providerStatusSchema,
  spendPerformed: z.literal(false),
  credentialsObserved: z.literal(false),
});

export const incidentSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  severity: severitySchema,
  impact: z.string().min(1),
  containment: z.string().nullable(),
  resolution: z.string().nullable(),
  ownerId: z.string().min(1),
  status: incidentStatusSchema,
  evidence: z.array(z.string()),
  openedAt: z.string().datetime(),
  resolvedAt: z.string().datetime().nullable(),
  provenance: provenanceSchema,
});

export const metricSampleSchema = z.object({
  id: z.string().min(1),
  name: z.enum([
    'workload_open_jobs',
    'sla_met_ratio',
    'cycle_time_minutes',
    'first_pass_acceptance',
    'human_minutes',
    'runner_success_ratio',
  ]),
  dimensions: z.record(z.string(), z.string()),
  value: z.number(),
  unit: z.string().min(1),
  observedAt: z.string().datetime(),
  confidence: z.enum(['EXACT', 'ESTIMATED', 'UNKNOWN']),
  provenance: provenanceSchema,
});

export const pauseControlSchema = z.object({
  id: z.string().min(1),
  scopeType: pauseScopeSchema,
  scopeId: z.string().min(1),
  paused: z.boolean(),
  reason: z.string().min(1),
  actorId: z.string().min(1),
  changedAt: z.string().datetime(),
  provenance: provenanceSchema,
});

export const companyDayRunSchema = z.object({
  id: z.literal('SYNTHETIC-COMPANY-DAY-01'),
  status: z.enum(['NOT_RUN', 'RUNNING', 'COMPLETED', 'FAILED']),
  currentStep: z.number().int().nonnegative(),
  totalSteps: z.number().int().nonnegative(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  command: z.literal('npm run scenario:company-day'),
  technicalDocumentationJobId: z.literal('OPS-DOC-001'),
  externalActionsPerformed: z.literal(false),
  deterministic: z.literal(true),
});

const emptyOperations = {
  schemaVersion: 1 as const,
  queues: [],
  dependencies: [],
  assignments: [],
  leases: [],
  runners: [],
  incidents: [],
  metrics: [],
  pauses: [],
  companyDay: {
    id: 'SYNTHETIC-COMPANY-DAY-01' as const,
    status: 'NOT_RUN' as const,
    currentStep: 0,
    totalSteps: 0,
    startedAt: null,
    completedAt: null,
    command: 'npm run scenario:company-day' as const,
    technicalDocumentationJobId: 'OPS-DOC-001' as const,
    externalActionsPerformed: false as const,
    deterministic: true as const,
  },
};

const emptyAccessControl = {
  schemaVersion: 1 as const,
  policyVersion: 2,
  policies: [],
  resources: [],
  expiringGrantIds: [],
  companyPaused: false,
  drill: {
    id: 'SYNTHETIC-EMERGENCY-DRILL-01',
    status: 'NOT_RUN' as const,
    startedAt: null,
    completedAt: null,
    correlationId: null,
    affectedJobIds: [],
    auditEventIds: [],
    evidencePath: null,
    synthetic: true as const,
  },
};

export const accessControlSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  policyVersion: z.number().int().positive(),
  policies: z.array(accessPolicySchema),
  resources: z.array(resourceAssetSchema),
  expiringGrantIds: z.array(z.string()),
  companyPaused: z.boolean(),
  drill: emergencyDrillSchema,
});

export const liveOperationsSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  queues: z.array(queueProjectionSchema),
  dependencies: z.array(jobDependencySchema),
  assignments: z.array(assignmentProjectionSchema),
  leases: z.array(leaseProjectionSchema),
  runners: z.array(runnerNodeSchema),
  incidents: z.array(incidentSchema),
  metrics: z.array(metricSampleSchema),
  pauses: z.array(pauseControlSchema),
  companyDay: companyDayRunSchema,
});

export const snapshotSourceSchema = z.object({
  mode: dataModeSchema,
  recordProvenance: z.enum(['SEEDED', 'SYNTHETIC_DEMO', 'LIVE_LOCAL']),
  generatedAt: z.string().datetime(),
  displayTimezone: z.literal('Europe/Berlin'),
});

export const companySnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  source: snapshotSourceSchema.default({
    mode: 'SYNTHETIC_DEMO',
    recordProvenance: 'SYNTHETIC_DEMO',
    generatedAt: '2026-08-09T12:40:00.000Z',
    displayTimezone: 'Europe/Berlin',
  }),
  project: z.object({
    name: z.literal('KI-Firma'),
    asOf: z.literal('2026-08-09'),
    day: z.literal(11),
    gate: z.literal('G2'),
    status: z.string().min(1),
    approvedBudgetEur: z.literal(30),
    actualSpendEur: z.literal(0),
    activeCandidates: z.literal(3),
    humanMinutesToday: z.number().int().nonnegative(),
  }),
  people: z.array(personSchema),
  departments: z.array(departmentSchema),
  jobs: z.array(jobSchema),
  grants: z.array(grantSchema),
  sessions: z.array(runnerSessionSchema),
  providerPools: z.array(providerPoolSchema),
  approvals: z.array(approvalGateSchema),
  events: z.array(domainEventSchema),
  notifications: z.array(notificationSchema),
  operations: liveOperationsSnapshotSchema.default(emptyOperations),
  accessControl: accessControlSnapshotSchema.default(emptyAccessControl),
});

export const policyDecisionSchema = z.object({
  decision: z.enum(['ALLOW', 'DENY', 'REQUIRE_GATE']),
  policyVersion: z.number().int().positive(),
  reasons: z.array(z.string()),
  requiredGateType: z.string().nullable(),
  constraints: z.array(z.string()),
  auditFields: z.array(z.string()),
});

export const actorContextSchema = z.object({
  actorId: z.enum(['HUM-001', 'HUM-002']),
  role: z.enum(['OWNER', 'BOUNDED_OPERATOR']),
  sessionId: z.string().min(1),
  scope: z.array(z.string()),
  budgetCeilingCents: z.number().int().nonnegative(),
});

export const exportPreviewSchema = z.object({
  format: z.enum(['markdown', 'json']),
  content: z.string(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/u),
  generatedAt: z.string().datetime(),
  writesPerformed: z.literal(false),
});

export type DataMode = z.infer<typeof dataModeSchema>;
export type Provenance = z.infer<typeof provenanceSchema>;
export type Person = z.infer<typeof personSchema>;
export type PersonNature = z.infer<typeof personNatureSchema>;
export type Department = z.infer<typeof departmentSchema>;
export type Job = z.infer<typeof jobSchema>;
export type JobStatus = z.infer<typeof jobStatusSchema>;
export type Grant = z.infer<typeof grantSchema>;
export type ResourceAsset = z.infer<typeof resourceAssetSchema>;
export type ResourceType = z.infer<typeof resourceTypeSchema>;
export type AccessPolicy = z.infer<typeof accessPolicySchema>;
export type EmergencyDrill = z.infer<typeof emergencyDrillSchema>;
export type AccessControlSnapshot = z.infer<typeof accessControlSnapshotSchema>;
export type RunnerSession = z.infer<typeof runnerSessionSchema>;
export type ProviderPool = z.infer<typeof providerPoolSchema>;
export type ApprovalGate = z.infer<typeof approvalGateSchema>;
export type DomainEvent = z.infer<typeof domainEventSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type QueueProjection = z.infer<typeof queueProjectionSchema>;
export type JobDependency = z.infer<typeof jobDependencySchema>;
export type AssignmentProjection = z.infer<typeof assignmentProjectionSchema>;
export type LeaseProjection = z.infer<typeof leaseProjectionSchema>;
export type RunnerNode = z.infer<typeof runnerNodeSchema>;
export type Incident = z.infer<typeof incidentSchema>;
export type MetricSample = z.infer<typeof metricSampleSchema>;
export type PauseControl = z.infer<typeof pauseControlSchema>;
export type CompanyDayRun = z.infer<typeof companyDayRunSchema>;
export type LiveOperationsSnapshot = z.infer<typeof liveOperationsSnapshotSchema>;
export type CompanySnapshot = z.infer<typeof companySnapshotSchema>;
export type PolicyDecision = z.infer<typeof policyDecisionSchema>;
export type ActorContext = z.infer<typeof actorContextSchema>;
export type ExportPreview = z.infer<typeof exportPreviewSchema>;
export type TaskDomain = z.infer<typeof taskDomainSchema>;
export type RunnerAdapterKey = z.infer<typeof runnerAdapterKeySchema>;
export type RunnerControlPayload = z.infer<typeof runnerControlPayloadSchema>;
export type SignedRunnerControlEnvelope = z.infer<typeof signedRunnerControlEnvelopeSchema>;
export type ProviderPolicyReviewStatus = z.infer<typeof providerPolicyReviewStatusSchema>;
export type CapacitySource = z.infer<typeof capacitySourceSchema>;
export type CapacityRemainingUnit = z.infer<typeof capacityRemainingUnitSchema>;
export type ProviderStatus = z.infer<typeof providerStatusSchema>;
export type DataClass = z.infer<typeof dataClassSchema>;
export type RunnerEnrollmentStatus = z.infer<typeof runnerEnrollmentStatusSchema>;
export type RunnerJobEnvelopePayload = z.infer<typeof runnerJobEnvelopePayloadSchema>;
export type SignedRunnerJobEnvelope = z.infer<typeof signedRunnerJobEnvelopeSchema>;
export type RunnerHeartbeatPayload = z.infer<typeof runnerHeartbeatPayloadSchema>;
export type SignedRunnerHeartbeat = z.infer<typeof signedRunnerHeartbeatSchema>;
export type ChangedFileManifestEntry = z.infer<typeof changedFileManifestEntrySchema>;
export type RunnerLifecycleEvent = z.infer<typeof runnerLifecycleEventSchema>;
export type RunnerExecutionResult = z.infer<typeof runnerExecutionResultSchema>;
